import { createApi } from '@reduxjs/toolkit/query/react'
import {
  IClient,
  IClientSettings,
  IClientWithSettings,
} from 'shared/models/Client'
import axiosBaseQuery from 'shared/utils/axiosBaseQuery'
import { getActiveClientId } from './selectors'
import { addToasterMessage } from '../toaster'
import { RootState } from '../store'
import { GET_CLIENT_TAG } from 'shared/constants/storeTags'
import { IPortalUserSummary } from 'Modules/Communication/PortalUsers/shared/models/PortalUsers'
import { axiosErrorMessageHelper } from 'shared/utils/axiosErrorMessageHelper'

const headers = {
  'x-api-version': '3.0',
}

const clientsApi = createApi({
  reducerPath: 'clients',
  tagTypes: [GET_CLIENT_TAG],
  baseQuery: axiosBaseQuery({ baseUrl: '/client/clients' }),
  endpoints: (build) => ({
    getClient: build.query<IClientWithSettings, string>({
      providesTags: [GET_CLIENT_TAG],
      // keep for 2 hours
      keepUnusedDataFor: 60 * 60 * 2,

      async queryFn(arg, _api, _extraOptions, query) {
        const client = await query({ url: `/${arg}`, method: 'GET' })

        if (client.error) return { error: 'Det gikk ikke å hente klienten' }

        const settings = await query({
          url: `/${arg}/settings`,
          method: 'GET',
          headers,
        })

        if (settings.error)
          return {
            error: 'Det gikk ikke å hente portaltilgjengelighet for klienten',
          }

        const portalAvailable = (settings.data as IClientSettings)
          .portalAvailableIds

        const constructedClient: IClientWithSettings = {
          ...(client.data as IClient),
          portalAvailable,
        }

        return { data: constructedClient }
      },
    }),

    updateAdvisorFullAccess: build.mutation<void, boolean>({
      invalidatesTags: [GET_CLIENT_TAG],
      async queryFn(allow, { dispatch, getState }, _extraOptions, query) {
        const state = getState() as RootState
        const id = getActiveClientId(state)

        if (!id)
          return {
            error: 'Det gikk ikke å hente klienten',
          }

        const result = await query({
          url: `/${id}/advisorportalaccess`,
          method: 'PUT',
          data: { allow },
        })

        if (result.error) {
          const errorMsg =
            'Det skjedde en feil ved endring av tilgangen for rådgiver'

          dispatch(
            addToasterMessage({
              text: errorMsg,
              type: 'success',
            })
          )

          clientsApi.util.updateQueryData('getClient', id, (data) => {
            data.advisorsGrantedFullAccessOnStyreportal = allow
          })

          return {
            error: errorMsg,
          }
        }

        return { data: undefined }
      },
    }),
    getPortalSummary: build.query<IPortalUserSummary, void>({
      providesTags: [GET_CLIENT_TAG],
      async queryFn(_arg, { getState }, _extraOptions, query) {
        const state = getState() as RootState
        const id = getActiveClientId(state)

        if (!id)
          return {
            error: 'En feil har oppstått i henting av selskapet',
          }

        const response = await query({
          url: `/${id}/portalusers/summary`,
          method: 'GET',
        })
        if (response.error) {
          const error = axiosErrorMessageHelper(response.error, {
            fallbackError:
              'En feil har oppstått i henting av portalbrukeroppsummering.',
          })
          return { error }
        }
        return { data: response.data as IPortalUserSummary }
      },
    }),
  }),
})

export default clientsApi
export const {
  useGetClientQuery,
  useLazyGetClientQuery,
  useUpdateAdvisorFullAccessMutation,
  useGetPortalSummaryQuery,
} = clientsApi
